import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import { PDFViewer } from "@react-pdf/renderer";

// --- FONT REGISTRATION (Kept from original for consistency) ---
Font.register({
  family: "Helvetica",
  fonts: [
    {
      src: "https://fonts.gstatic.com/s/helveticaneue/v70/1Ptsg8zYS_SKggPNyC0IT0kLW-43aMEzIO6XUTLjad8.woff2",
    },
    {
      src: "https://fonts.gstatic.com/s/helveticaneue/v70/1Ptsg8zYS_SKggPNyC0IT0kLW-43aMEzIO6XUTLjad8.woff2",
      fontWeight: "bold",
    },
  ],
});

// --- HELPER FUNCTIONS ---
// Helper to format dates from YYYY-MM-DD or full ISO string
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    // Reformat DD MM YYYY to YYYY-MM-DD for constructor
    const parts = dateString.split(" ");
    if (parts.length === 3) {
      const [day, month, year] = parts;
      dateString = `${year}-${month}-${day}`;
    }
    const options = { year: "numeric", month: "2-digit", day: "2-digit" };
    return new Date(dateString).toLocaleDateString("en-GB", options);
  } catch (e) {
    return dateString; // Return original if formatting fails
  }
};

// Helper to format currency
const formatCurrency = (amount) => {
  if (typeof amount !== "number") return "0.00";
  return amount.toFixed(2);
};

// --- STYLES FOR THE INVOICE ---
const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    fontSize: 11,
    padding: 30,
    backgroundColor: "#ffffff",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottom: 2,
    borderColor: "#c0c0c0",
    paddingBottom: 10,
    marginBottom: 20,
  },
  companyName: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#333",
  },
  invoiceInfo: {
    textAlign: "right",
  },
  invoiceTitle: {
    fontSize: 28,
    fontWeight: "bold",
    marginBottom: 5,
    color: "#333",
  },
  invoiceDetails: {
    fontSize: 10,
    color: "#555",
  },
  customerInfo: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 30,
  },
  addressBlock: {
    width: "48%",
  },
  addressTitle: {
    fontWeight: "bold",
    fontSize: 12,
    borderBottom: 1,
    borderColor: "#333",
    paddingBottom: 4,
    marginBottom: 6,
  },
  addressText: {
    fontSize: 10,
    lineHeight: 1.5,
  },
  table: {
    display: "table",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#c0c0c0",
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  tableRow: {
    flexDirection: "row",
    backgroundColor: "#ffffff",
  },
  tableHeader: {
    backgroundColor: "#f2f2f2",
    borderBottomWidth: 2,
    borderColor: "#c0c0c0",
  },
  tableCol: {
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#c0c0c0",
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 6,
  },
  tableCell: {
    fontSize: 10,
  },
  headerCell: {
    fontSize: 10,
    fontWeight: "bold",
  },
  colIndex: { width: "5%" },
  colDesc: { width: "40%" },
  colQty: { width: "8%" },
  colPrice: { width: "15%" },
  colDiscount: { width: "12%" },
  colTotal: { width: "20%", textAlign: "right" },
  textRight: { textAlign: "right" },
  totals: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 20,
  },
  totalsTable: {
    width: "40%",
  },
  totalsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 4,
    borderBottom: 1,
    borderColor: "#e0e0e0",
  },
  totalsLabel: {
    fontSize: 11,
  },
  totalsValue: {
    fontSize: 11,
    fontWeight: "bold",
  },
  grandTotalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 6,
    marginTop: 5,
    backgroundColor: "#f2f2f2",
  },
  grandTotalLabel: {
    fontSize: 12,
    fontWeight: "bold",
  },
  grandTotalValue: {
    fontSize: 12,
    fontWeight: "bold",
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: "center",
    fontSize: 10,
    color: "grey",
  },
});

// --- COMPONENT FOR A SINGLE INVOICE PAGE ---
const SingleInvoicePage = ({ order, companyDetails }) => {
  // Use static data based on the image for any missing fields
  const safeOrder = {
    id: "N/A",
    created_at: new Date().toISOString(),
    name: "Customer Name",
    billing_address: "Customer Billing Address",
    shipping_address: "Customer Shipping Address",
    phone: "Customer Phone",
    payment_method: "N/A",
    line_items: [],
    subtotal: 0,
    total_shipping: 0,
    total_discounts: 0,
    total_amount: 0,
    ...order,
  };

  // Calculate subtotal from products (before discount)
  const subtotal = (order?.products || []).reduce(
    (acc, item) => acc + (item?.salePrice || 0) * (item?.quantity || 1),
    0
  );
  
  // Calculate total discount from products
  const totalDiscount = (order?.products || []).reduce(
    (acc, item) => acc + (item?.discount || 0),
    0
  );

  const printDate = new Date()
    .toLocaleDateString("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
    .replace(",", "");

  return (
    <Page size="A4" style={styles.page}>
      {/* Header */}
      <View style={styles.header}>
        <View>
          <Text style={styles.companyName}>
            {companyDetails?.name || "Sam'sClub"}
          </Text>
        </View>
        <View style={styles.invoiceInfo}>
          <Text style={styles.invoiceTitle}>INVOICE</Text>
          <Text style={styles.invoiceDetails}>Invoice #: {safeOrder.order_number || safeOrder.id}</Text>
          <Text style={styles.invoiceDetails}>
            Order Date: {formatDate(safeOrder.created_at)}
          </Text>
          <Text style={styles.invoiceDetails}>Print Date: {printDate}</Text>
          <Text style={styles.invoiceDetails}>
            Payment Method: {safeOrder.payment_method}
          </Text>
        </View>
      </View>

      {/* Customer Information */}
      <View style={styles.customerInfo}>
        <View style={styles.addressBlock}>
          <Text style={styles.addressTitle}>BILL TO</Text>
          <Text style={styles.addressText}>{safeOrder.name}</Text>
          <Text style={styles.addressText}>{safeOrder.billing_address}</Text>
          <Text style={styles.addressText}>Phone: {safeOrder.phone}</Text>
        </View>
        <View style={styles.addressBlock}>
          <Text style={styles.addressTitle}>SHIP TO</Text>
          <Text style={styles.addressText}>{safeOrder.name}</Text>
          <Text style={styles.addressText}>{safeOrder.shipping_address}</Text>
          <Text style={styles.addressText}>Phone: {safeOrder.phone}</Text>
        </View>
      </View>

      {/* Items Table */}
      <View style={styles.table}>
        {/* Table Header */}
        <View style={[styles.tableRow, styles.tableHeader]}>
          <View style={[styles.tableCol, styles.colIndex]}>
            <Text style={styles.headerCell}>#</Text>
          </View>
          <View style={[styles.tableCol, styles.colDesc]}>
            <Text style={styles.headerCell}>Product Name</Text>
          </View>
          <View style={[styles.tableCol, styles.colQty]}>
            <Text style={[styles.headerCell, styles.textRight]}>Qty</Text>
          </View>
          <View style={[styles.tableCol, styles.colPrice]}>
            <Text style={[styles.headerCell, styles.textRight]}>
              Unit Price
            </Text>
          </View>
          <View style={[styles.tableCol, styles.colDiscount]}>
            <Text style={[styles.headerCell, styles.textRight]}>
              Discount
            </Text>
          </View>
          <View style={[styles.tableCol, styles.colTotal]}>
            <Text style={styles.headerCell}>Total</Text>
          </View>
        </View>
        {/* Table Body */}
        {order?.products?.map((item, index) => (
          <View style={styles.tableRow} key={item.id || index}>
            <View style={[styles.tableCol, styles.colIndex]}>
              <Text style={styles.tableCell}>{index + 1}</Text>
            </View>
            <View style={[styles.tableCol, styles.colDesc]}>
              <Text style={styles.tableCell}>{item?.title}</Text>
            </View>
            <View style={[styles.tableCol, styles.colQty]}>
              <Text style={[styles.tableCell, styles.textRight]}>
                {item?.quantity || 1}
              </Text>
            </View>
            <View style={[styles.tableCol, styles.colPrice]}>
              <Text style={[styles.tableCell, styles.textRight]}>
                {formatCurrency(item?.salePrice)}
              </Text>
            </View>
            <View style={[styles.tableCol, styles.colDiscount]}>
              <Text style={[styles.tableCell, styles.textRight]}>
                {formatCurrency(item?.discount || 0)}
              </Text>
            </View>
            <View style={[styles.tableCol, styles.colTotal]}>
              <Text style={styles.tableCell}>
                {formatCurrency((item?.salePrice * (item?.quantity || 1)) - (item?.discount || 0))}
              </Text>
            </View>
          </View>
        ))}
      </View>

      {/* Totals Section */}
      <View style={styles.totals}>
        <View style={styles.totalsTable}>
          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>Subtotal</Text>
            <Text style={styles.totalsValue}>
              {formatCurrency(subtotal)}
            </Text>
          </View>
          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>Shipping Cost</Text>
            <Text style={styles.totalsValue}>
              {formatCurrency(order?.shipping_amount || 0)}
            </Text>
          </View>
          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>Discount</Text>
            <Text style={styles.totalsValue}>
              -{formatCurrency(totalDiscount)}
            </Text>
          </View>
          {order?.vat_per > 0 && (
            <View style={styles.totalsRow}>
              <Text style={styles.totalsLabel}>VAT ({order?.vat_per}%)</Text>
              <Text style={styles.totalsValue}>
                {formatCurrency(order?.vat_amount || 0)}
              </Text>
            </View>
          )}
          <View style={styles.grandTotalRow}>
            <Text style={styles.grandTotalLabel}>TOTAL</Text>
            <Text style={styles.grandTotalValue}>
              {formatCurrency(safeOrder.total_amount)}
            </Text>
          </View>
        </View>
      </View>

      <Text style={styles.footer}>Thank you for your business!</Text>
    </Page>
  );
};

// --- MAIN INVOICE COMPONENT ---
// It takes an array of orders and maps them to pages
const Invoice = ({ orders = [], companyDetails }) => {
  // Mock data for a single order if `orders` prop is empty, based on the provided image
  const mockOrder = {
    id: "224122105268938",
    created_at: "26 08 2025",
    name: "Arham",
    billing_address:
      "Arham house no.14, agha Nadeem street, new shalimar road, Punjab Lahore - Samnabad, Samnabad Pakistan",
    shipping_address:
      "Arham house no.14, agha Nadeem street, new shalimar road, Punjab, Lahore - Samnabad, Samnabad Pakistan",
    phone: "9203244670087",
    payment_method: "COD",
    line_items: [
      {
        id: 1,
        name: "JOLOCHIPS Hottest Chip Madness | Last CHIP Challenge Chips 5 gram",
        sku: "619520788_PK-2892858108",
        quantity: 1,
        price: 1999.0,
      },
    ],
    total_shipping: 160.0,
    total_discounts: 0.0,
    total_amount: 2159.0,
  };

  const ordersToDisplay = orders.length > 0 ? orders : [mockOrder];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <div style={{ flex: 1 }}>
        <PDFViewer width="100%" height="100%">
          <Document>
            {ordersToDisplay.map((order, index) => (
              <SingleInvoicePage
                key={index}
                order={order}
                companyDetails={companyDetails}
              />
            ))}
          </Document>
        </PDFViewer>
      </div>
    </div>
  );
};

export default Invoice;

